<template>
  <div>
    <el-row type="flex" justify="end" class="title-bar">
      <div>
        <w-signal-strength-legend class="mr-r4"/>
        <el-button size="small" @click="onClickExportExcel"><img src="@/assets/img/icons/excel.png" style="position:relative;top:3px;left:-2px"> 엑셀출력</el-button>
        <w-header-search-button @click="showSearchTool = !showSearchTool"></w-header-search-button>
      </div>
    </el-row>
    <el-row>
      <el-table-wrapper
        class="inner-table"
        v-loading="isLoading"
        ref="accessPointTable"
        border
        :data="tableData"
        :columns="commonColumns"
        :show-custom-header="true"
        :show-search-tool="showSearchTool"
        :show-overflow-tooltip="true"
        :pagination="true"
        :remote="true"
        :total="tableTotal"
        :column-default="columnDefault"
        @condition-change="onChangeTable"
        @expand-change="onExpandChange"
      >
        <template #obj-name-slot="scope">
          <span v-if="scope.row.objName" class="link c-pointer" @click="clickObjName(scope.row)">{{ scope.row.objName }}</span>
          <span v-else>-</span>
        </template>
        <template #wireless-expand-slot="scope">
          <w-ap-client-table table-class="popup" :data="scope.row.clients" :loading="scope.row.apLoading" :show-search-tool="false" :obj-company-id="scope.row.objCompanyId" />
        </template>
        <template slot-scope="scope" slot="cpu-slot">
          <w-rsc-perf-event-progress-bar v-if="!isNil(scope.row['DEVICE_PERF/MEM_USERATE'])" :perfRecords="scope.row" perfKey="DEVICE_PERF/MEM_USERATE" />
          <span v-else>{{ EMPTY_TEXT }}</span>
        </template>
        <template slot-scope="scope" slot="rssi-slot">
          <i :class="getSignalStrengthIconClass(scope.row['WIRELESS_ACCESSPOINT/AP_RSSI'])" />
        </template>
        <template slot-scope="scope" slot="memory-slot">
          <w-rsc-perf-event-progress-bar v-if="!isNil(scope.row['DEVICE_PERF/MEM_USERATE'])" :perfRecords="scope.row" perfKey="DEVICE_PERF/MEM_USERATE" />
          <span v-else>{{ EMPTY_TEXT }}</span>
        </template>
        <template slot-scope="scope" slot="icmp-slot">
          <span> {{ convertSlotValue(scope.row['RESPONSETIME/RESPONSETIME_ICMP']) }}</span>
        </template>
        <template slot-scope="scope" slot="snmp-slot">
          <span> {{ convertSlotValue(scope.row['RESPONSETIME/RESPONSETIME_SNMP']) }}</span>
        </template>
      </el-table-wrapper>
    </el-row>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { isNil, isEmpty, findIndex } from 'lodash';
import { columnDefault } from '@/constants/table';
import { menuDefine } from '@/constants/menu';
import { performanceNodeTabs } from '@/constants/performance';
import { EMPTY_TEXT, getValueAfterCheckEmpty } from '@/utils/string';
import { convertValuByUnit, convertValueForInterfaceSpeed } from '@/utils/number';
import { objtype } from '@/constants/objtype';
import msgUtil from '@/utils/message';
import { getSignalStrengthIconClass } from '@/utils/wireless-device';
import { routePathByObjId } from '@/utils/router';
import commonAPI from '@/api/performance/common';
import FileApi from '@/api/common/file';
import WSignalStrengthLegend from '@/views/performance/node/analysis/wireless-ap/components/SignalStrengthLegend';
import WApClientTable from './ApClientTable';

export default {
  name: 'WAccessPointPerfTable',
  props: {
    active: {
      type: Boolean,
      defulat: () => false,
    },
  },
  components: {
    WApClientTable,
    WSignalStrengthLegend,
  },
  data() {
    return {
      isLoading: false,
      tableData: [],
      columnDefault,
      tableTotal: 0,
      showSearchTool: false,
      commonColumns: [
        {
          type: 'expand',
          scopedSlot: 'wireless-expand-slot',
        },
        {
          prop: 'objName',
          label: '장비명',
          scopedSlot: 'obj-name-slot',
          headerAlign: 'center',
          sortable: true,
          searchable: true,
          remoteProp: 'o.obj_name',
          formatter: this.getTableValueAfterCheckEmpty,
        },
        {
          prop: 'ipaddress',
          label: 'IP',
          headerAlign: 'center',
          sortable: true,
          searchable: true,
          width: 120,
          formatter: this.getTableValueAfterCheckEmpty,
        },
        {
          prop: 'objMacaddress',
          label: 'MAC',
          headerAlign: 'center',
          sortable: true,
          searchable: true,
          width: 120,
          remoteProp: 'o.obj_macaddress',
          formatter: this.getTableValueAfterCheckEmpty,
        },
        {
          prop: 'objCompanyName',
          label: '제조사',
          headerAlign: 'center',
          sortable: true,
          searchable: true,
          width: 120,
          remoteProp: 'oc.obj_company_name',
          formatter: this.getTableValueAfterCheckEmpty,
        },
        {
          prop: 'objProductId',
          label: '모델명',
          headerAlign: 'center',
          sortable: true,
          searchable: true,
          remoteProp: 'o.obj_product_id',
          formatter: this.getTableValueAfterCheckEmpty,
        },
        {
          prop: 'ssid',
          label: 'WLAN',
          remoteProp: 'ncmInfo.ssid',
          headerAlign: 'center',
          sortable: true,
          searchable: true,
          formatter: this.getTableValueAfterCheckEmpty,
        },
        {
          prop: 'radioBand',
          label: '주파수 대역',
          remoteProp: 'ncmInfo.radioBand',
          headerAlign: 'center',
          sortable: true,
          searchable: true,
          formatter: this.getTableValueAfterCheckEmpty,
        },
        {
          prop: 'apGroup',
          label: 'AP 그룹',
          remoteProp: 'ncmInfo.apGroup',
          headerAlign: 'center',
          sortable: true,
          searchable: true,
          formatter: this.getTableValueAfterCheckEmpty,
        },
        {
          prop: 'WIRELESS_ACCESSPOINT/CLIENT_COUNT',
          label: '접속자 수',
          headerAlign: 'center',
          sortable: true,
          searchable: false,
          formatter: this.getTableValueAfterCheckEmpty,
        },
        {
          label: '트래픽 (bps)',
          headerAlign: 'center',
          childColumns: [
            {
              prop: 'WIRELESS_ACCESSPOINT/AP_TRAFFIC_BPS_IN',
              sortable: true,
              label: 'IN',
              headerAlign: 'center',
              width: 100,
              formatter: this.convertValue,
            },
            {
              prop: 'WIRELESS_ACCESSPOINT/AP_TRAFFIC_BPS_OUT',
              sortable: true,
              label: 'OUT',
              headerAlign: 'center',
              width: 100,
              formatter: this.convertValue,
            },
          ],
        },
        {
          prop: 'WIRELESS_ACCESSPOINT/AP_RSSI',
          label: '신호세기',
          headerAlign: 'center',
          scopedSlot: 'rssi-slot',
          sortable: true,
          searchable: false,
          formatter: this.getTableValueAfterCheckEmpty,
        },
        {
          prop: 'WIRELESS_ACCESSPOINT/AP_SNR',
          label: 'SNR',
          headerAlign: 'center',
          sortable: true,
          searchable: false,
          formatter: this.getTableValueAfterCheckEmpty,
        },
      ],
      EMPTY_TEXT,
    };
  },
  computed: {
    customerId() {
      return this.$store.state.common.customerId;
    },
    ...mapGetters(['selectedNode', 'selectedObjDefineIds', 'selectedObjDefineIdsForPhysical']),
  },
  watch: {
    selectedNode: {
      handler() {
        if (this.$refs.netdeviceTable === undefined) {
          this.$nextTick(() => {
            this.loadStore();
          });
        } else {
          this.loadStore();
        }
      },
    },
    selectedObjDefineIdsForPhysical: {
      handler() {
        this.loadStore();
      },
    },
    active: {
      handler() {
        this.loadStore();
      },
    },
  },
  methods: {
    getSignalStrengthIconClass,
    /**
     * 성능/운영 > 루트/그룹 > 무선 AP 성능정보를 조회한다.
     * @author yelim
     * @return {[type]} [description]
     */
    loadStore() {
      if (this.active) {
        this.isLoading = true;
        const treeType = this.$route.path.indexOf('physical') > 0 ? 'physical' : 'logical';
        const tableParams = this.$refs.accessPointTable.getParameter();
        const params = {
          managegroupIds: this.selectedNode.childManagegroupIds,
          managetypeId: 'NMS',
          objtypeId: objtype.WIRELESS_AP,
          objDefineId: this.selectedObjDefineIds[0],
          treeType,
          tableType: 'standard',
          objCompanyId: this.selectedNode.objCompanyId,
          ...tableParams,
        };
        if (!isEmpty(this.selectedObjDefineIdsForPhysical)) {
          params.objCompanyIds = this.selectedObjDefineIdsForPhysical;
        }
        commonAPI
          .getPerformanceGroupList(params)
          .then((result) => {
            if (result.data.success) {
              const resultData = result.data.data;
              resultData.forEach((item) => {
                item.isExpand = false;
                item.apLoading = false;
                item.clients = null;
              });
              this.tableData = resultData;
              this.tableTotal = result.data.total;
            }
          })
          .catch(() => {})
          .finally(() => {
            this.isLoading = false;
          });
      }
    },
    getTableValueAfterCheckEmpty(row, column, value) {
      return getValueAfterCheckEmpty(value);
    },
    convertValue(row, column, value) {
      return isNil(value) ? EMPTY_TEXT : convertValueForInterfaceSpeed(value);
    },
    convertCValue(value) {
      return isNil(value) ? EMPTY_TEXT : convertValuByUnit(value, '℃', false);
    },
    convertSlotValue(value) {
      return isNil(value) ? EMPTY_TEXT : convertValuByUnit(value, null);
    },
    clickObjName(row) {
      if (!isNil(row.objId)) {
        routePathByObjId(this, row.objId, menuDefine.PERF_OPER, performanceNodeTabs.summary.name, isEmpty(this.selectedNode) ? undefined : this.selectedNode.childManagegroupIds, null, false);
      }
    },
    onChangeTable() {
      this.loadStore();
    },
    /**
     * 무선 AP 정보를 펼치면 클라이언트 정보를 조회한다.
     * @author yelim
     * @param  {[type]} rowInfo [description]
     * @return {[type]}         [description]
     */
    onExpandChange(rowInfo) {
      const findIdx = findIndex(this.tableData, (item) => item.objId === rowInfo.objId);
      // 선택한 AP의 클라이언트 정보를 조회할 때
      // 1) 이미 조회가 된 경우, row를 닫고 정보를 초기화한다.
      // 2) 조회되지 않은 경우, row를 열고 선택한 row의 objId 정보를 가지고 목록을 조회한다.
      if (!rowInfo.isExpand) {
        rowInfo.isExpand = true;
        this.tableData[findIdx].apLoading = true;
        commonAPI
          .getClientInfoByWireless({
            objId: rowInfo.objId,
          })
          .then((result) => {
            if (result.data.success) {
              const resultData = result.data.data;
              if (!isNil(resultData)) {
                this.tableData[findIdx].clients = resultData;
              }
            }
          })
          .catch((e) => {
            this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '클라이언트 정보', false));
            throw new Error(e);
          })
          .finally(() => {
            this.tableData[findIdx].apLoading = false;
          });
      } else {
        rowInfo.isExpand = false;
        this.tableData[findIdx].clients = [];
      }
    },
    /**
     * 그리드 데이터 엑셀출력
     * @author NaJY
     * @return {[type]}         [description]
     */
    onClickExportExcel() {
      const treeType = this.$route.path.indexOf('physical') > 0 ? 'physical' : 'logical';
      const tableParams = this.$refs.accessPointTable.getParameter();
      const params = {
        managegroupIds: this.selectedNode.childManagegroupIds,
        managetypeId: 'NMS',
        objtypeId: objtype.WIRELESS_AP,
        objDefineId: this.selectedObjDefineIds[0],
        treeType,
        tableType: 'standard',
        objCompanyId: this.selectedNode.objCompanyId,
        ...tableParams,
      };
      if (!isEmpty(this.selectedObjDefineIdsForPhysical)) {
        params.objCompanyIds = this.selectedObjDefineIdsForPhysical;
      }

      commonAPI.exportWirelessApGroupPerfInfo(params)
        .then((response) => {
          if (response.data.success && !isNil(response.data.data)) {
            FileApi.downloadExportedExcelFile(response.data.data);
          } else {
            this.$message(msgUtil.getResultMsgData('출력', '엑셀', false));
          }
        })
        .catch(() => {
          this.$message(msgUtil.getResultMsgData('출력', '엑셀', false));
        });
    },
  },
};
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/views/performance/components/wireless/AccessPointPerfTable.vue